import React, { useEffect, useState, useRef } from 'react';
import App from '../App';
import { parseBookingLocation } from '../utils/bookingUrl';
import { createBookingEntry } from '../utils/bookingEntry';
import { resolveProperty } from '../services/propertyResolver';
import { apiService } from '../services/apiService';
import { readSession } from '../config';

export default function PropertyEntry() {
  const [location, setLocation] = useState(() => ({ pathname: window.location.pathname, search: window.location.search }));
  const locationRef = useRef(location);
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    const snapshot = () => ({ pathname: window.location.pathname, search: window.location.search });
    const onLocation = () => {
      const next = snapshot();
      if (locationRef.current.pathname !== next.pathname || locationRef.current.search !== next.search) {
        locationRef.current = next;
        setResult(null);
        setLocation(next);
      }
    };
    const onUrlUpdated = () => { locationRef.current = snapshot(); };
    window.addEventListener('bookingurlupdated', onUrlUpdated);
    window.addEventListener('popstate', onLocation);
    window.addEventListener('propertynavigate', onLocation);
    return () => {
      window.removeEventListener('bookingurlupdated', onUrlUpdated);
      window.removeEventListener('popstate', onLocation);
      window.removeEventListener('propertynavigate', onLocation);
    };
  }, []);
  useEffect(() => {
    let current = true;
    setResult(null);
    setError(null);
    (async () => {
      try {
        // Restore the property pathname for a root payment return in this tab.
        const callback = new URLSearchParams(window.location.search);
        if (window.location.pathname === '/' && callback.has('payment_status')) {
          try {
            const pending = JSON.parse(readSession('pendingBookingProperty'));
            const matching = pending && ((callback.get('reservation_id') && String(pending.reservationId) === callback.get('reservation_id')) ||
              (callback.get('booking_reference') && pending.bookingReference === callback.get('booking_reference')));
            if (matching && Date.now() - pending.timestamp >= 0 && Date.now() - pending.timestamp < 86400000 &&
                /^\/(?:[a-zA-Z0-9_-]+\/?)?$/.test(pending.pathname)) {
              // Widgets stay on /. Their payment gateway return may omit the
              // cleaned identity query, so recover the exact pending shop pair.
              if (pending.pathname === '/' && pending.shop_id && pending.shop_code && !callback.has('shop_code')) {
                callback.set('shop_id', String(pending.shop_id));
                callback.set('shop_code', pending.shop_code);
              }
              window.history.replaceState({}, '', pending.pathname + '?' + callback.toString());
            }
          } catch { /* A callback must still work when recovery data is missing. */ }
        }
        const entry = parseBookingLocation(window.location);
        const property = await resolveProperty(entry);
        if (!current) return;
        const initialized = createBookingEntry(property, entry);
        apiService.clearPropertyCache();
        setResult({ ...initialized, entry, id: `${window.location.pathname}:${location.search}:${attempt}` });
      } catch (failure) {
        if (current) setError(failure.message || 'Unable to load this property.');
      }
    })();
    return () => { current = false; };
  }, [location, attempt]);
  if (result) return <App key={result.id} propertyEntry={result} />;
  return <main className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
    <div className="max-w-lg text-center" role={error ? 'alert' : 'status'}>
      <h1 className="text-2xl font-bold mb-4">{error ? 'Unable to load this property' : 'Loading your hotel…'}</h1>
      {error && <><p className="mb-5">{error}</p><button className="rounded bg-gray-900 text-white px-5 py-2" onClick={() => setAttempt(value => value + 1)}>Try again</button> <a className="underline ml-4" href="/">Return home</a></>}
    </div>
  </main>;
}
